import { CampaignConfigSchema, ConfigSelectComponent } from '../types';

/**
 * Build the `applicationIds` value for a create/update body.
 * Returns undefined when there are none (matches original behavior).
 */
export const toApplicationIds = (config: CampaignConfigSchema): string[] | undefined =>
  Array.isArray(config.applicationIds) && config.applicationIds.length > 0
    ? config.applicationIds
    : undefined;

/**
 * Convert the editor's CampaignConfigSchema back into the backend config shape.
 * Pure: pass `nowIso` for a deterministic `updatedAt`.
 *
 * Ported from the original CampaignConfigBuilder.tsx handleSave (lines ~508-688),
 * with added serialization of the recurring / text-to-win template settings.
 */
export const toBackendConfig = (config: CampaignConfigSchema, nowIso: string): CampaignConfigSchema => {
  const campaignConfig: CampaignConfigSchema = {
    icon: config.icon,
    name: config.name,
    type: config.type,
    category: config.category,
    description: config.description,
    updatedAt: nowIso,
    keywords: config.keywords,
    recipients: config.recipients,
    messages: config.messages,
    metadata: config.metadata,
    opt_out_message: config.opt_out_message,
    schedule: config.schedule ?? { value: 'immediately', visible: false, required: false },
    appointment_type: config.appointment_type,
    contact_date_type: config.contact_date_type,
    attach_calendar_invite: config.attach_calendar_invite,
    message: {
      text: { visible: true, required: true, value: null },
      types: [],
      subject: { visible: true, required: false, value: null },
      data_capture: {
        active: false,
        visible: false,
        dropdown: true,
        required: true,
        description: 'Capture response to message as',
        metadata_name: null,
      },
    },
    messageTemplates: config.messageTemplates,
    isPoll: config.isPoll,
  };

  // Merge values from shared components (groups, keywords, appointment type)
  config.components?.forEach(component => {
    if (!component.visible) return;

    switch (component.name) {
      case 'groups-field':
        campaignConfig.recipients = {
          ...campaignConfig.recipients,
          values:
            (component as ConfigSelectComponent).values || campaignConfig.recipients?.values || [],
        };
        break;
      case 'keywords-field':
        campaignConfig.keywords = {
          ...campaignConfig.keywords,
          values:
            (component as ConfigSelectComponent).values || campaignConfig.keywords?.values || [],
        };
        break;
      case 'appointment-type':
        campaignConfig.appointment_type = {
          ...campaignConfig.appointment_type,
          value:
            (component as ConfigSelectComponent).values?.[0] ||
            campaignConfig.appointment_type?.value ||
            null,
        };
        break;
    }
  });

  // Message templates -> backend message
  if (config.messageTemplates && config.messageTemplates.length > 0) {
    const firstTemplate = config.messageTemplates[0];
    if (firstTemplate.messageType === 'mms-only' || firstTemplate.messageType === 'sms-or-mms') {
      campaignConfig.message = {
        text: {
          visible: true,
          required: true,
          value: firstTemplate.mmsText || firstTemplate.smsText || null,
        },
        types: ['mms'],
        subject: {
          visible: true,
          required: false,
          value: firstTemplate.mmsSubject || null,
        },
        data_capture: firstTemplate.dataCapture?.active
          ? {
              active: true,
              visible: true,
              dropdown: true,
              required: true,
              description:
                firstTemplate.dataCapture.description || 'Capture response to message as',
              metadata_name: null,
            }
          : {
              active: false,
              visible: false,
              dropdown: true,
              required: true,
              description: 'Capture response to message as',
              metadata_name: null,
            },
      };
    }

    campaignConfig.messageTemplates = config.messageTemplates;
  } else if (campaignConfig.message) {
    // Legacy: derive message from components
    const message = campaignConfig.message;
    config.components?.forEach(component => {
      if (!component.visible) return;

      switch (component.name) {
        case 'sms-message':
          if (component.type === 'sms' && 'messageText' in component) {
            message.text.value = component.messageText || null;
            message.text.required = component.required;
            if (!message.types.includes('sms')) {
              message.types.push('sms');
            }
          }
          break;
        case 'mms-message':
          if (component.type === 'mms' && 'messageText' in component && 'subject' in component) {
            message.text.value = component.messageText || null;
            message.subject.value = component.subject || null;
            message.text.required = component.required;
            message.subject.required = component.required;
            if (!message.types.includes('mms')) {
              message.types.push('mms');
            }
          }
          break;
        case 'data-capture':
          if (component.type === 'checkbox') {
            message.data_capture.active = true;
            message.data_capture.visible = component.visible;
            message.data_capture.required = component.required;
          }
          break;
        case 'opt-out-message':
          if (component.type === 'text' && 'defaultText' in component) {
            campaignConfig.opt_out_message = {
              value: component.defaultText || '',
              active: campaignConfig.opt_out_message?.active || false,
              visible: component.visible,
              required: component.required,
            };
          }
          break;
        case 'schedule-dropdown':
          if (component.type === 'select' && 'values' in component) {
            campaignConfig.schedule = {
              value: component.values?.[0] || 'immediately',
              visible: component.visible,
              required: component.required,
            };
          }
          break;
      }
    });

    message.text.visible = message.types.length > 0;
    message.subject.visible = message.types.includes('mms');
  }

  // Serialize type-specific template settings only for their owning type
  if (config.type === 'recurring' && config.recurringSettings) {
    campaignConfig.recurringSettings = config.recurringSettings;
  }
  if (config.type === 'text_to_win' && config.textToWinSettings) {
    campaignConfig.textToWinSettings = config.textToWinSettings;
  }
  if (config.type === 'enrollment' && config.enrollmentSettings) {
    campaignConfig.enrollmentSettings = config.enrollmentSettings;
  }

  return campaignConfig;
};
